import { useState, useMemo } from 'react'
import styled from 'styled-components'

import Box from 'components/Box'
import Icon from 'components/Icon'
import NavigationTab from 'components/NavigationTab'
import { ThemeColor } from 'utils/colors'
import { isPreview } from 'utils/url'

export type Tab = {
  label: string
  tabId: string
  tabContent: JSX.Element
  tabWidth?: string
  superScript?: string
  isWideBorder?: boolean
  onClick?: () => void
  defaultTabLabelColor?: ThemeColor
  superScriptColor?: ThemeColor
}

type CollapsibleNavigationTabsProps = {
  tabs: Tab[]
  defaultActiveTab?: string
  isCollapsedByDefault?: boolean
  hideCollapseIcon?: boolean
  currentTabId?: string
  onChange?: (tabId: string) => void
  activeTabFontColor?: ThemeColor
  tabContainerMarginLeft?: string
  hideTabContainerBorder?: boolean
  isAddonTab?: boolean
}
const CollapsibleNavigationTabs = ({
  tabs,
  defaultActiveTab,
  isCollapsedByDefault = false,
  hideCollapseIcon,
  currentTabId,
  onChange,
  activeTabFontColor,
  tabContainerMarginLeft,
  hideTabContainerBorder = false,
  isAddonTab,
}: CollapsibleNavigationTabsProps) => {
  const isOnPreview = isPreview()

  const activeTab = useMemo(
    () => tabs.find((t) => t.tabId === currentTabId) ?? tabs.find((t) => t.tabId === defaultActiveTab) ?? tabs[0],
    [tabs, currentTabId, defaultActiveTab],
  )

  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault)

  const handleTabClick = (tab: Tab) => {
    onChange?.(tab.tabId)
    tab.onClick?.()
  }

  return (
    <Container>
      <Box display="flex" width="100%" cursor={isOnPreview ? 'not-allowed' : 'default'}>
        {!hideCollapseIcon && (
          <CollapseIcon
            data-testid="test-id-tabs-collapse-icon"
            color="gray5"
            icon={['fa', 'caret-down']}
            mr="10px"
            $isCollapsed={isCollapsed}
            onClick={() => setIsCollapsed((v) => !v)}
          />
        )}
        <TabsContainer
          isOnPreview={isOnPreview}
          tabContainerMarginLeft={tabContainerMarginLeft}
          hideBorder={hideTabContainerBorder}
        >
          {tabs.map((tab) => {
            const isActive = tab.tabId === activeTab.tabId

            return (
              <TabContainer
                key={tab.tabId}
                active={isActive}
                tabWidth={tab.tabWidth}
                isOnPreview={isOnPreview}
                onClick={() => handleTabClick(tab)}
              >
                <NavigationTab
                  {...tab}
                  active={isActive}
                  activeTabFontColor={activeTabFontColor}
                  variant={isAddonTab ? 'addon' : 'collapsible'}
                  fullwidth={tab.isWideBorder}
                />
              </TabContainer>
            )
          })}
        </TabsContainer>
      </Box>

      {!isCollapsed && <TabContent>{activeTab.tabContent}</TabContent>}
    </Container>
  )
}

const TabsContainer = styled.div<{
  tabContainerMarginLeft?: string
  hideBorder?: boolean
  isOnPreview?: boolean
}>`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  ${({ hideBorder = false, theme }) =>
    hideBorder ? 'border: none;' : `border-bottom: 1px solid ${theme.colors.gray4};`}
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-right: 2px;
  margin-left: ${({ tabContainerMarginLeft }) => tabContainerMarginLeft};

  ${(p) =>
    p.isOnPreview
      ? `
        pointer-events: none;
        cursor: not-allowed !important;    
        
        * {
            pointer-events: none;
            cursor: not-allowed !important;
          }
      `
      : ''}
`

const TabContainer = styled.div<{
  active: boolean
  tabWidth?: string
  isOnPreview?: boolean
}>`
  display: flex;

  ${({ tabWidth }) =>
    tabWidth &&
    `width:${tabWidth};
    justify-content: center;
    align-items: flex-end;`}

  ${(p) =>
    p.isOnPreview
      ? `
        pointer-events: none;
        cursor: not-allowed;

        * {
            pointer-events: none;
            cursor: not-allowed;
          }
      `
      : ''}
`

const CollapseIcon = styled(Icon)<{ $isCollapsed: boolean }>`
  transform: ${(p) => (p.$isCollapsed ? 'rotate(0deg)' : 'rotate(-180deg)')};
  transition: transform 0.3s;
  align-self: center;
`

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const Container = styled.div``

export default CollapsibleNavigationTabs
